import { apiClient } from "./client";

// ─── Types ─────────────────────────────────────────────────────────────────

export type CommunityVisibility = "public" | "private";
export type ChannelType =
  | "text"
  | "announcement"
  | "threads"
  | "forum"
  | "events";

export interface CommunityResponse {
  id: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  bannerUrl: string | null;
  accentColor: string | null;
  visibility: CommunityVisibility;
  ownerId: string;
  slug: string;
  memberCount: number;
  rules: string | null;
  requireMfaForMod: boolean;
  deletedAt: number | null;
  createdAt: number;
  updatedAt: number;
  myMembership?: MemberResponse;
}

export interface CategoryResponse {
  id: string;
  communityId: string;
  name: string;
  position: number;
  createdAt: number;
  updatedAt: number;
}

export interface ChannelOverride {
  targetType: "role" | "member";
  targetId: string;
  allow: string;
  deny: string;
}

export interface ChannelResponse {
  id: string;
  communityId: string;
  categoryId: string | null;
  type: ChannelType;
  name: string;
  topic: string | null;
  position: number;
  slowModeSeconds: number;
  overrides: ChannelOverride[];
  externallyOpen: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface RoleResponse {
  id: string;
  communityId: string;
  name: string;
  color: string | null;
  position: number;
  /** BigInt-encoded permission bitmask as a base-10 string. */
  permissions: string;
  hoisted: boolean;
  mentionable: boolean;
  defaultKind: "owner" | "admin" | "moderator" | "member" | null;
  createdAt: number;
  updatedAt: number;
}

export interface MemberResponse {
  communityId: string;
  userId: string;
  roleIds: string[];
  nickname: string | null;
  avatarUrl: string | null;
  joinedAt: number;
  timeoutUntil: number | null;
  mutedUntil: number | null;
}

export interface MemberListItem extends MemberResponse {
  displayName: string;
}

export interface MessageResponse {
  id: string;
  communityId: string;
  channelId: string;
  authorId: string;
  content: unknown;
  replyToId: string | null;
  reactions: Array<{ emoji: string; userIds: string[] }>;
  isPinned: boolean;
  isEdited: boolean;
  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface InviteResponse {
  code: string;
  communityId: string;
  channelId: string | null;
  roleIds: string[];
  createdBy: string;
  maxUses: number | null;
  uses: number;
  expiresAt: number | null;
  createdAt: number;
}

export interface CommunityPageResponse {
  community: CommunityResponse;
  categories: CategoryResponse[];
  channels: ChannelResponse[];
  roles: RoleResponse[];
  myMembership: MemberResponse | null;
}

// ─── CRUD ──────────────────────────────────────────────────────────────────

export interface CreateCommunityInput {
  name: string;
  description?: string;
  visibility?: CommunityVisibility;
  iconUrl?: string;
  accentColor?: string;
}

export async function createCommunity(
  input: CreateCommunityInput
): Promise<CommunityResponse> {
  return apiClient.post<CommunityResponse>("/api/communities", input);
}

export async function listMyCommunities(): Promise<{
  items: CommunityResponse[];
}> {
  return apiClient.get<{ items: CommunityResponse[] }>("/api/communities/mine");
}

export async function getCommunity(id: string): Promise<CommunityPageResponse> {
  return apiClient.get<CommunityPageResponse>(`/api/communities/${id}`);
}

export interface UpdateCommunityInput {
  name?: string;
  description?: string | null;
  visibility?: CommunityVisibility;
  iconUrl?: string | null;
  bannerUrl?: string | null;
  accentColor?: string | null;
  rules?: string | null;
  requireMfaForMod?: boolean;
}

export async function updateCommunity(
  id: string,
  input: UpdateCommunityInput
): Promise<CommunityResponse> {
  return apiClient.put<CommunityResponse>(`/api/communities/${id}`, input);
}

export async function deleteCommunity(id: string): Promise<CommunityResponse> {
  return apiClient.delete<CommunityResponse>(`/api/communities/${id}`);
}

export async function restoreCommunity(id: string): Promise<CommunityResponse> {
  return apiClient.post<CommunityResponse>(`/api/communities/${id}/restore`, {});
}

export async function transferCommunity(
  id: string,
  newOwnerId: string
): Promise<{ newOwner: MemberResponse; previousOwner: MemberResponse }> {
  return apiClient.post<{ newOwner: MemberResponse; previousOwner: MemberResponse }>(
    `/api/communities/${id}/transfer`,
    { newOwnerId }
  );
}

// ─── Categories + channels ─────────────────────────────────────────────────

export async function createCategory(
  id: string,
  input: { name: string; position?: number }
): Promise<CategoryResponse> {
  return apiClient.post<CategoryResponse>(`/api/communities/${id}/categories`, input);
}

export async function updateCategory(
  id: string,
  categoryId: string,
  input: { name?: string; position?: number }
): Promise<CategoryResponse> {
  return apiClient.put<CategoryResponse>(
    `/api/communities/${id}/categories/${categoryId}`,
    input
  );
}

export async function deleteCategory(id: string, categoryId: string): Promise<void> {
  await apiClient.delete(`/api/communities/${id}/categories/${categoryId}`);
}

export interface CreateChannelInput {
  name: string;
  type?: ChannelType;
  categoryId?: string | null;
  topic?: string;
  position?: number;
  slowModeSeconds?: number;
}

export async function createChannel(
  id: string,
  input: CreateChannelInput
): Promise<ChannelResponse> {
  return apiClient.post<ChannelResponse>(`/api/communities/${id}/channels`, input);
}

export async function updateChannel(
  id: string,
  channelId: string,
  input: Partial<CreateChannelInput> & { overrides?: ChannelOverride[] }
): Promise<ChannelResponse> {
  return apiClient.put<ChannelResponse>(
    `/api/communities/${id}/channels/${channelId}`,
    input
  );
}

export async function deleteChannel(id: string, channelId: string): Promise<void> {
  await apiClient.delete(`/api/communities/${id}/channels/${channelId}`);
}

// ─── Messages ──────────────────────────────────────────────────────────────

export async function createCommunityMessage(
  id: string,
  channelId: string,
  input: { content: unknown; replyToId?: string }
): Promise<MessageResponse> {
  return apiClient.post<MessageResponse>(
    `/api/communities/${id}/channels/${channelId}/messages`,
    input
  );
}

export async function listCommunityMessages(
  id: string,
  channelId: string,
  options: { limit?: number; before?: string } = {}
): Promise<{
  items: MessageResponse[];
  nextCursor: string | null;
  hasMore: boolean;
}> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.before) params.set("before", options.before);
  const qs = params.toString();
  return apiClient.get<{
    items: MessageResponse[];
    nextCursor: string | null;
    hasMore: boolean;
  }>(
    `/api/communities/${id}/channels/${channelId}/messages${qs ? `?${qs}` : ""}`
  );
}

export async function updateCommunityMessage(
  id: string,
  channelId: string,
  messageId: string,
  input: { content?: unknown; isPinned?: boolean }
): Promise<MessageResponse> {
  return apiClient.put<MessageResponse>(
    `/api/communities/${id}/channels/${channelId}/messages/${messageId}`,
    input
  );
}

export async function deleteCommunityMessage(
  id: string,
  channelId: string,
  messageId: string
): Promise<void> {
  await apiClient.delete(
    `/api/communities/${id}/channels/${channelId}/messages/${messageId}`
  );
}

export interface ScheduledMessageResponse {
  id: string;
  communityId: string;
  channelId: string;
  authorId: string;
  content: unknown;
  replyToId: string | null;
  scheduledFor: number;
  createdAt: number;
}

/** Queue a message to send at `scheduledFor` (unix ms). Perm-gated server-side. */
export async function createScheduledMessage(
  id: string,
  channelId: string,
  input: { content: unknown; replyToId?: string; scheduledFor: number }
): Promise<ScheduledMessageResponse> {
  return apiClient.post<ScheduledMessageResponse>(
    `/api/communities/${id}/channels/${channelId}/scheduled`,
    input
  );
}

export async function listScheduledMessages(
  id: string,
  channelId: string
): Promise<{ items: ScheduledMessageResponse[] }> {
  return apiClient.get<{ items: ScheduledMessageResponse[] }>(
    `/api/communities/${id}/channels/${channelId}/scheduled`
  );
}

export async function deleteScheduledMessage(
  id: string,
  channelId: string,
  schedId: string
): Promise<void> {
  await apiClient.delete(
    `/api/communities/${id}/channels/${channelId}/scheduled/${schedId}`
  );
}

export async function toggleCommunityReaction(
  id: string,
  channelId: string,
  messageId: string,
  emoji: string
): Promise<MessageResponse> {
  return apiClient.post<MessageResponse>(
    `/api/communities/${id}/channels/${channelId}/messages/${messageId}/reactions`,
    { emoji }
  );
}

export async function markChannelRead(
  id: string,
  channelId: string,
  messageId: string,
  messageTs?: number
): Promise<{ ok: boolean; lastReadAt: number }> {
  return apiClient.post<{ ok: boolean; lastReadAt: number }>(
    `/api/communities/${id}/channels/${channelId}/read`,
    {
      messageId,
      ...(messageTs !== undefined ? { messageTs } : {}),
    }
  );
}

export async function listReadState(
  id: string
): Promise<{
  items: Array<{
    channelId: string;
    lastReadMessageId: string;
    lastReadAt: number;
  }>;
}> {
  return apiClient.get<{
    items: Array<{
      channelId: string;
      lastReadMessageId: string;
      lastReadAt: number;
    }>;
  }>(`/api/communities/${id}/read-state`);
}

export interface AuditLogEntry {
  id: string;
  actorId: string;
  action: string;
  detail: Record<string, unknown> | null;
  reason: string | null;
  createdAt: number;
}

export async function listCommunityAudit(
  id: string,
  options: { limit?: number; before?: string } = {}
): Promise<{ items: AuditLogEntry[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.before) params.set("before", options.before);
  const qs = params.toString();
  return apiClient.get<{
    items: AuditLogEntry[];
    nextCursor: string | null;
  }>(`/api/communities/${id}/audit${qs ? `?${qs}` : ""}`);
}

// ─── Reports ───────────────────────────────────────────────────────────────

export interface ReportResponse {
  id: string;
  channelId: string;
  messageId: string;
  reporterId: string;
  reason: string;
  status: "open" | "resolved" | "dismissed";
  resolvedBy: string | null;
  resolvedAt: number | null;
  createdAt: number;
}

export async function reportMessage(
  id: string,
  input: { channelId: string; messageId: string; reason: string }
): Promise<ReportResponse> {
  return apiClient.post<ReportResponse>(`/api/communities/${id}/reports`, input);
}

export async function listReports(
  id: string,
  status?: "open" | "resolved" | "dismissed"
): Promise<{ items: ReportResponse[] }> {
  const qs = status ? `?status=${status}` : "";
  return apiClient.get<{ items: ReportResponse[] }>(
    `/api/communities/${id}/reports${qs}`
  );
}

export async function resolveReport(
  id: string,
  reportId: string,
  status: "resolved" | "dismissed"
): Promise<ReportResponse> {
  return apiClient.post<ReportResponse>(
    `/api/communities/${id}/reports/${reportId}/resolve`,
    { status }
  );
}

// ─── Notification preferences ──────────────────────────────────────────────

export type NotifLevel = "all" | "mentions" | "muted" | "default";

export interface NotifPrefEntry {
  scope: string;
  level: NotifLevel;
  mutedUntil: number | null;
  /** True = mute is overridden for @-mentions of this user. */
  alwaysOnMention?: boolean;
  updatedAt: number;
}

export async function setNotifPref(
  id: string,
  input: {
    scope: string;
    level: NotifLevel;
    mutedUntil?: number | null;
    alwaysOnMention?: boolean;
  }
): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(
    `/api/communities/${id}/notif-pref`,
    input
  );
}

export async function listNotifPrefs(
  id: string
): Promise<{ items: NotifPrefEntry[] }> {
  return apiClient.get<{ items: NotifPrefEntry[] }>(
    `/api/communities/${id}/notif-pref`
  );
}

// ─── Search ────────────────────────────────────────────────────────────────

export async function searchCommunityMessages(
  id: string,
  q: string,
  limit?: number
): Promise<{ items: MessageResponse[] }> {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set("limit", String(limit));
  return apiClient.get<{ items: MessageResponse[] }>(
    `/api/communities/${id}/search?${params.toString()}`
  );
}

export async function searchAllCommunityMessages(
  q: string,
  limit?: number
): Promise<{
  items: Array<{ communityId: string; message: MessageResponse }>;
}> {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set("limit", String(limit));
  return apiClient.get<{
    items: Array<{ communityId: string; message: MessageResponse }>;
  }>(`/api/communities/search?${params.toString()}`);
}

// ─── Polls (vote on a poll-content message) ────────────────────────────────

export async function voteMessagePoll(
  id: string,
  channelId: string,
  messageId: string,
  optionId: string
): Promise<MessageResponse> {
  return apiClient.post<MessageResponse>(
    `/api/communities/${id}/channels/${channelId}/messages/${messageId}/poll-vote`,
    { optionId }
  );
}

// ─── Media uploads ─────────────────────────────────────────────────────────

export interface CommunityUploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  expiresIn: number;
}

export async function getCommunityUploadUrl(
  id: string,
  channelId: string,
  input: { contentType: string; fileSize: number; fileName?: string }
): Promise<CommunityUploadUrlResponse> {
  return apiClient.post<CommunityUploadUrlResponse>(
    `/api/communities/${id}/channels/${channelId}/upload-url`,
    input
  );
}

export async function getCommunityDownloadUrl(
  id: string,
  channelId: string,
  objectKey: string
): Promise<{ url: string; expiresIn: number; expiresAt: string }> {
  return apiClient.post<{ url: string; expiresIn: number; expiresAt: string }>(
    `/api/communities/${id}/channels/${channelId}/download-url`,
    { objectKey }
  );
}

// ─── Events ────────────────────────────────────────────────────────────────

export interface CommunityEvent {
  id: string;
  communityId: string;
  channelId: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: number;
  endAt: number | null;
  createdBy: string;
  rsvps: Record<string, "going" | "maybe" | "declined">;
  createdAt: number;
  updatedAt: number;
}

export async function createCommunityEvent(
  id: string,
  input: {
    channelId: string;
    title: string;
    description?: string;
    location?: string;
    startAt: number;
    endAt?: number;
  }
): Promise<CommunityEvent> {
  return apiClient.post<CommunityEvent>(
    `/api/communities/${id}/events`,
    input
  );
}

export async function listCommunityEvents(
  id: string,
  channelId?: string
): Promise<{ items: CommunityEvent[] }> {
  const qs = channelId ? `?channelId=${encodeURIComponent(channelId)}` : "";
  return apiClient.get<{ items: CommunityEvent[] }>(
    `/api/communities/${id}/events${qs}`
  );
}

export async function rsvpCommunityEvent(
  id: string,
  eventId: string,
  status: "going" | "maybe" | "declined" | "clear"
): Promise<CommunityEvent> {
  return apiClient.post<CommunityEvent>(
    `/api/communities/${id}/events/${eventId}/rsvp`,
    { status }
  );
}

// ─── Automod + slug ────────────────────────────────────────────────────────

export interface AutomodRules {
  blockedWords: string[];
  blockedLinkDomains: string[];
}

export async function getCommunityAutomod(
  id: string
): Promise<AutomodRules> {
  return apiClient.get<AutomodRules>(`/api/communities/${id}/automod`);
}

export async function updateCommunityAutomod(
  id: string,
  input: AutomodRules
): Promise<AutomodRules> {
  return apiClient.put<AutomodRules>(`/api/communities/${id}/automod`, input);
}

export async function setCommunitySlug(
  id: string,
  slug: string
): Promise<{ slug: string }> {
  return apiClient.put<{ slug: string }>(`/api/communities/${id}/slug`, {
    slug,
  });
}

/** End-to-end uploader: presign → PUT → return the object key. The
 *  channel-view composer calls this then sends a message with content
 *  `{type: "image", objectKey, ...}`. */
export async function uploadCommunityAsset(
  id: string,
  kind: "icon" | "banner",
  file: File
): Promise<{ publicUrl: string; objectKey: string }> {
  const presign = await apiClient.post<{
    uploadUrl: string;
    objectKey: string;
    publicUrl: string;
  }>(`/api/communities/${id}/asset-upload-url`, {
    kind,
    contentType: file.type,
    fileSize: file.size,
  });
  const putResponse = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.status}`);
  }
  return { publicUrl: presign.publicUrl, objectKey: presign.objectKey };
}

export async function listChannelPins(
  id: string,
  channelId: string
): Promise<{ items: MessageResponse[] }> {
  return apiClient.get<{ items: MessageResponse[] }>(
    `/api/communities/${id}/channels/${channelId}/pins`
  );
}

export async function uploadCommunityMedia(
  id: string,
  channelId: string,
  file: File
): Promise<{ objectKey: string }> {
  const presign = await getCommunityUploadUrl(id, channelId, {
    contentType: file.type,
    fileSize: file.size,
    fileName: file.name,
  });
  const putResponse = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.status}`);
  }
  return { objectKey: presign.objectKey };
}

// ─── Roles + members ───────────────────────────────────────────────────────

export interface CreateRoleInput {
  name: string;
  color?: string;
  position?: number;
  permissions?: string;
  hoisted?: boolean;
  mentionable?: boolean;
}

export async function createRole(
  id: string,
  input: CreateRoleInput
): Promise<RoleResponse> {
  return apiClient.post<RoleResponse>(`/api/communities/${id}/roles`, input);
}

export async function updateRole(
  id: string,
  roleId: string,
  input: Partial<CreateRoleInput>
): Promise<RoleResponse> {
  return apiClient.put<RoleResponse>(
    `/api/communities/${id}/roles/${roleId}`,
    input
  );
}

export async function deleteRole(id: string, roleId: string): Promise<void> {
  await apiClient.delete(`/api/communities/${id}/roles/${roleId}`);
}

export async function listMembers(
  id: string,
  options: { limit?: number } = {}
): Promise<{ items: MemberListItem[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  const qs = params.toString();
  return apiClient.get<{
    items: MemberListItem[];
    nextCursor: string | null;
  }>(`/api/communities/${id}/members${qs ? `?${qs}` : ""}`);
}

export interface UpdateMemberInput {
  nickname?: string | null;
  avatarUrl?: string | null;
  roleIds?: string[];
  timeoutMs?: number;
  muteMs?: number;
}

export async function updateMember(
  id: string,
  userId: string,
  input: UpdateMemberInput
): Promise<MemberResponse> {
  return apiClient.put<MemberResponse>(
    `/api/communities/${id}/members/${userId}`,
    input
  );
}

export async function removeMember(
  id: string,
  userId: string,
  options: { action?: "kick" | "ban" | "leave"; reason?: string } = {}
): Promise<{ removed: boolean; action: string }> {
  return apiClient.delete<{ removed: boolean; action: string }>(
    `/api/communities/${id}/members/${userId}` +
      (Object.keys(options).length
        ? `?${new URLSearchParams(
            Object.entries(options).reduce<Record<string, string>>((acc, [key, val]) => {
              if (val !== undefined) acc[key] = String(val);
              return acc;
            }, {})
          ).toString()}`
        : "")
  );
}

// ─── Invites + discovery ───────────────────────────────────────────────────

export interface CreateInviteInput {
  channelId?: string;
  roleIds?: string[];
  maxUses?: number;
  /** Unix ms expiry. */
  expiresAt?: number;
}

export async function createInvite(
  id: string,
  input: CreateInviteInput = {}
): Promise<InviteResponse> {
  return apiClient.post<InviteResponse>(`/api/communities/${id}/invites`, input);
}

export async function listInvites(
  id: string
): Promise<{ items: InviteResponse[] }> {
  return apiClient.get<{ items: InviteResponse[] }>(
    `/api/communities/${id}/invites`
  );
}

export async function deleteInvite(id: string, code: string): Promise<void> {
  await apiClient.delete(`/api/communities/${id}/invites/${code}`);
}

export async function peekInvite(code: string): Promise<{
  invite: InviteResponse;
  community: CommunityResponse;
}> {
  return apiClient.get<{
    invite: InviteResponse;
    community: CommunityResponse;
  }>(`/api/communities/invites/${code}`);
}

export async function acceptInvite(code: string): Promise<{
  community: CommunityResponse;
  member: MemberResponse;
}> {
  return apiClient.post<{
    community: CommunityResponse;
    member: MemberResponse;
  }>(`/api/communities/join/${code}`, {});
}

export async function discoverCommunities(
  options: { q?: string; limit?: number } = {}
): Promise<{ items: CommunityResponse[] }> {
  const params = new URLSearchParams();
  if (options.q) params.set("q", options.q);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  const qs = params.toString();
  return apiClient.get<{ items: CommunityResponse[] }>(
    `/api/communities/discover${qs ? `?${qs}` : ""}`
  );
}
